import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
  useMemo,
} from "react"
import {
  useLocation as useLocationLib,
  useNavigationType,
} from "react-router-dom"
import type { AppViewSheetKind } from "@/v2-backoffice-client/app/AppRouter"
import type { VStackEntry } from "@/v2-backoffice-client/app/AppViewSheetBreadcrumb"

const BreadcrumbStackContext = createContext<VStackEntry[]>([])

function parseVEntry(value: string | null) {
  const o = {
    kind: null as AppViewSheetKind,
    id: null as string | null,
  }

  if (!value) return o

  const colon = value.indexOf(":")
  if (colon === -1) return o

  return {
    kind: value.slice(0, colon) as AppViewSheetKind,
    id: value.slice(colon + 1),
  }
}

export function useAppViewSheetBreadcrumb(value: string | null) {
  const stack = useContext(BreadcrumbStackContext)
  const current = useMemo(() => parseVEntry(value), [value])
  return {
    current,
    stack,
  }
}

export type AppViewSheetBreadcrumbProviderProps = { children: ReactNode }

function AppViewSheetBreadcrumbProvider(
  props: AppViewSheetBreadcrumbProviderProps,
) {
  const { children } = props
  const location = useLocationLib()
  const navigationType = useNavigationType()
  const vParam = new URLSearchParams(location.search).get("v")

  const [stack, setStack] = useState<VStackEntry[]>(() => {
    const { kind, id } = parseVEntry(vParam)
    return kind && id ? [{ kind, id }] : []
  })

  const stackRef = useRef(stack)
  const prevVParamRef = useRef(vParam)
  const historyRef = useRef<Map<string, VStackEntry[]>>(new Map())

  useEffect(() => {
    const key = location.key

    if (navigationType === "POP") {
      const saved = historyRef.current.get(key)
      if (saved) {
        stackRef.current = saved
        setStack(saved)
      }
      prevVParamRef.current = vParam
      return
    }

    if (vParam === prevVParamRef.current) {
      historyRef.current.set(key, stackRef.current)
      return
    }
    prevVParamRef.current = vParam

    const { kind, id } = parseVEntry(vParam)
    const isPush =
      (location.state as { vshistory?: string } | null)?.vshistory === "push"

    let next: VStackEntry[]
    if (!kind || !id) {
      next = []
    } else if (isPush && stackRef.current.length > 0) {
      next = [...stackRef.current, { kind, id }]
    } else {
      next = [{ kind, id }]
    }

    stackRef.current = next
    historyRef.current.set(key, next)
    setStack(next)
  }, [vParam, location.key, location.state, navigationType])

  return (
    <BreadcrumbStackContext.Provider value={stack}>
      {children}
    </BreadcrumbStackContext.Provider>
  )
}

export default AppViewSheetBreadcrumbProvider
